import { localized } from '@/i18n/localize'
export const leaseStrings = localized({
  title: 'Leases',
  filterAll: 'All',
  filterActive: 'Active',
  filterEndingSoon: 'Ending soon',
  filterPaymentPending: 'Payment pending',
  searchHint: 'Search tenant or property',
  emptyTitle: 'No leases yet',
  emptyDescription: 'Create a lease to start collecting rent.',
  filterEmptyTitle: 'No leases match',
  upToDate: 'Up to date',
  due: (amount: string) => `Due ${amount}`,
  endingSoonTitle: (count: number) => (count === 1 ? '1 lease ends within a month' : `${count} leases end within a month`),
  endingSoonAction: 'Review them',
  daysLeft: (n: number) => (n === 1 ? '1 day left' : `${n} days left`),
  monthsLeft: (n: number) => (n === 1 ? '1 month left' : `${n} months left`),
  endsToday: 'Ends today',
  remind: 'Remind',
  remindSent: 'Reminder sent',
  history: 'Lease history',
  addTenant: 'Add Tenant',
  tenants: 'Tenants',
  tenant: 'Tenant',
  newLease: 'New lease',
  retryPayment: 'Retry Payment',
  deletePending: 'Delete',
  deletePendingTitle: 'Delete this lease?',
  deletePendingDescription: 'The unpaid lease will be removed and the property becomes vacant again.',
  deleted: 'Lease deleted.',
  colProperty: 'Property',
  colTenant: 'Tenant',
  colRent: 'Rent',
  colTerm: 'Term',
  colStatus: 'Status',
  colDue: 'Due',
  view: 'View',
  colEnds: 'Ends',
  colEnded: 'Ended',
  colCollected: 'Collected',
  filterProperty: 'Property',
  allProperties: 'All properties',

  status: { active: 'Active', expired: 'Expired', canceled: 'Cancelled', 'cancellation-requested': 'Cancellation requested', 'payment-pending': 'Payment Pending' } as Record<string, string>,
  paymentPendingBadge: 'Payment Pending',
  // A tenant's offline payment awaiting the landlord's acceptance.
  awaitingConfirmationBadge: 'To confirm',
  awaitingConfirmationTitle: (amount: string) => `${amount} awaiting your confirmation`,
  awaitingConfirmationNote: 'Your tenant recorded this payment offline. Accept it on the Rent tab to mark the rent received.',
  awaitingConfirmationTenantTitle: (amount: string) => `${amount} awaiting confirmation`,
  awaitingConfirmationTenantNote: 'You have recorded this payment. Your landlord will confirm it.',
  openRentTab: 'Open Rent',

  wizardTitle: 'New lease',
  stepOf: (c: number, t: number) => `Step ${c} of ${t}`,
  next: 'Next',
  back: 'Back',
  close: 'Close',
  stepProperty: 'Property',
  stepTenant: 'Tenant',
  stepTerms: 'Terms',
  stepExtras: 'Extras',
  stepReview: 'Review',
  propertyMissing: 'Pick the property this lease is for',
  tenantMissing: "Add the tenant's mobile number and name",
  termsMissing: 'Set the rent and how long the lease runs',
  propertyHint: 'Only properties without a running lease can be let again.',
  noProperties: 'No Properties!',
  tenantKnown: "We already know this tenant — their name is filled in from their account.",
  extrasNote: 'All optional. You can add these later from the lease.',
  tenantPhoto: 'Tenant photo',
  changePhoto: 'Choose a photo',
  removePhoto: 'Remove',
  fileTooLarge: 'File too large. Please choose a smaller image.',
  countryHint: 'Country',
  mobileLabel: 'Mobile',
  mobileHint: '0000000000',
  alternateMobileLabel: 'Alternate Mobile',
  firstNameLabel: 'First Name',
  firstNameHint: 'Asha',
  lastNameLabel: 'Last Name',
  lastNameHint: 'Rao',
  invalidMobile: 'Please enter a valid mobile number',
  recentTenants: 'Recent tenants',
  startDateLabel: 'Lease Start Date',
  durationLabel: 'Lease Duration',
  durationCount: 'Number of periods',
  frequencyLabel: 'Rent Frequency',
  // No glyph: the field shows the property's own currency next to the amount.
  rentLabel: 'Rent',
  depositLabel: 'Deposit',
  propertyTypeLabel: 'Property Type',
  totalRentNote: (amount: string, count: number) => `${amount} over ${count} payments`,
  reviewTitle: 'Check the lease',
  chosenProperty: 'Chosen Property',
  chosenTenant: 'Chosen Tenant',
  leaseDetails: 'Lease Details',
  totalRent: 'Rent over the term',
  feeLabel: 'Activation fee',
  feeFree: 'No charge',
  feeFirstFree: 'Your first lease is free',
  seePlans: 'See plans for a lower lease fee',
  feeYears: (n: number) => (n === 1 ? '1 year block' : `${n} year blocks`),
  quoteFailed: 'Could not work out the fee. Tap to try again.',
  // LP-993: year-by-year fee when lease credits (or a promo code) pay part of it.
  feeByCredits: 'Paid with lease credits',
  feeByPromo: 'Covered by your code',
  feeYearLabel: (n: number) => `Year ${n}`,
  feeYearCredit: '1 credit',
  feeYearFreeLease: 'Free lease',
  feeYearDiscount: 'Code applied',
  feeCreditsUsed: (used: number, left: number) => `${used === 1 ? '1 credit' : `${used} credits`} used · ${left} left`,
  feeCreditsNote: 'Credits can’t be refunded once the lease is created.',
  createdWithCredits: (n: number) => (n === 1 ? 'Paid with 1 lease credit' : `Paid with ${n} lease credits`),
  retry: 'Retry',
  submitPay: 'Pay and activate',
  submitFree: 'Create lease',
  // The lease is created first (payment-pending) and the checkout opens after (LP-655).
  payNote: 'The lease is created now and stays payment-pending until the fee is paid. If the payment does not go through, retry or delete it from the Leases list.',
  edit: 'Edit',
  createdTitle: 'Lease created',
  createdBody: (name: string) => `${name} can see it in their app now.`,
  createdViewLease: 'View lease',
  createdAddAnother: 'Create another',
  freeFirstLeaseTitle: 'This Lease is Free!',
  activatedTitle: 'Lease Activated',
  activatedDescription: 'Your lease is now active and the tenant has been notified.',
  optional: 'Optional',
  summaryTitle: 'Summary',
  notChosen: 'Not chosen yet',
  propertyPick: 'Property',
  propertySearch: 'Search your properties',
  findTenant: 'Find an existing tenant',
  findTenantHint: 'Search by name or mobile',
  noTenantsFound: 'No tenant matches',
  orNewTenant: 'Or add a new tenant by mobile number',
  leaseTermLabel: 'Lease term',
  termFrom: 'Starts',
  termTo: 'Ends',
  termSnapped: (end: string, count: number, period: string) => `Ends ${end} · ${count} ${period.toLowerCase()}${count === 1 ? '' : 's'}`,
  pendingAfterFailure: 'The lease was created but not paid. Retry the payment from the Leases list.',
  unsavedTitle: 'Discard this lease?',
  unsavedMessage: 'Nothing has been saved yet.',
  discard: 'Discard',
  keepEditing: 'Keep editing',
  draftRestored: 'Draft restored.',
  somethingWentWrong: 'Something went wrong',

  detailTitle: 'Lease details',
  more: 'More',
  releaseAction: 'Release lease',
  releaseTitle: 'Release this lease?',
  releaseDescription: 'The tenant loses access and the property becomes vacant. Rent already recorded is kept.',
  releaseConfirmWord: 'RELEASE',
  released: 'Lease released.',
  endedOn: 'Ended on',
  paymentPendingNote: "This lease won't be fully active until payment is completed.",
  setupInProgressTitle: 'Lease setup in progress',
  setupInProgressNote: "Your landlord is still completing this lease. You'll be notified once it's active.",
  setupStepPay: 'Pay the lease fee',
  setupStepActivate: 'Lease activates',
  setupStepCollect: 'Collect rent',
  archivedTitle: 'Property Archived',
  archivedDescription: 'This property was archived due to a subscription downgrade. Upgrade your plan to restore access.',
  tabOverview: 'Overview',
  tabRent: 'Rent',
  tabMaintenance: 'Maintenance',
  tabDocuments: 'Documents',
  rentThisMonth: 'Rent this month',
  rentNoneThisMonth: 'No rent falls due this month.',
  rentHistoryLabel: 'Recent months',
  rentPartial: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  pending: 'Pending',
  scheduled: 'Upcoming',
  canceled: 'Cancelled',
  recordPayment: 'Record payment',
  payNow: 'Pay Now',
  viewProperty: 'View property',
  landlord: 'Landlord',
  call: 'Call',
  whatsapp: 'WhatsApp',
  agreementTitle: 'Lease Agreement',
  agreementSubtitle: 'Create & manage rental agreement',
  propertyDocuments: 'Property documents',
  documentLabels: { electricityBill: 'Electricity bill', taxBill: 'Tax bill', index2: 'Index II' } as Record<string, string>,
  open: 'Open',
  noRentsYet: 'No rents yet',
  noRentsDescription: 'Rents will appear here once the lease is active',
  rentDetails: 'Rent details',
  rentDate: 'Rent date',
  expected: 'Expected',
  paidAmount: 'Paid',
  balance: 'Balance',
  status_: 'Status',
  receipt: 'Receipt',
  receiptFailed: "Couldn't fetch the receipt. Please try again.",
  rentLedgerHint: 'The full ledger with running totals arrives with the rent module.',
  monthsPaid: (paid: string, total: number) => `${paid} of ${total} months paid`,
  collectedSoFar: 'collected so far',
  ofExpected: (amount: string) => `of ${amount}`,
  rentStatus: { paid: 'Paid', partiallyPaid: 'Partially Paid', due: 'Due', overdue: 'Overdue', upcoming: 'Upcoming', pending: 'Processing', cancelled: 'Cancelled' } as Record<string, string>,
  rentReceiptTitle: 'Rent receipt',
  rentReceiptFor: 'Rent for',
  rentReceiptDueBy: 'Due by',
  rentReceiptExpected: 'Rent for the month',
  rentReceiptStillOwed: 'Still owed',
  rentReceiptReference: 'Reference',
  rentReceiptNote: 'Generated by LeazePro',
  transactionAmount: 'Transaction Amount',
  coversMonths: 'Covers Months',
  method: 'Method',
  paidOn: 'Paid on',
  shareReceipt: 'Share Receipt',
  downloadReceipt: 'Download PDF',
  linkCopied: 'Receipt link copied.',
  receiptError: 'Could not load receipt. Please try again.',
  acceptPayment: 'Accept Payment',
  acceptPaymentTitle: 'Accept this payment?',
  acceptPaymentDescription: 'This will mark the rent as received.',
  acceptPaymentMultiMonth: (months: string) => `This payment covers ${months}. All these months will be marked as received.`,
  acceptPaymentError: 'Failed to accept payment. Please try again.',
  paymentAccepted: 'Payment marked as received',
  cashPaymentPendingNote: 'This payment will be marked as pending until the landlord confirms receipt.',
  accept: 'Accept',
  cancel: 'Cancel',
  collected: 'Collected',
  dueLabel: 'Due',
  exportCsv: 'Export CSV',
  print: 'Print',
  colMonth: 'Month',
  colExpected: 'Expected',
  colPaid: 'Paid',
  colBalance: 'Balance',
  colPaidOn: 'Paid on',
  modeLabels: { online: 'Online', offline: 'Cash' } as Record<string, string>,
  tabPayments: 'Payments',
  home: 'Home',
  contact: 'Contact',
  keyFacts: 'Key facts',
  nextDue: 'Next due',
  nothingDue: 'Nothing due',
  outstanding: 'Outstanding',
  collectedOfTerm: 'Collected over the term',
  termLabel: 'Term',
  frequency: 'Frequency',
  agreementStage: 'Agreement',
  agreementNone: 'No agreement yet',
  agreementNoneNote: 'Create one on LeazePro or upload a signed copy.',
  agreementNoneNoteUploadOnly: 'Upload a signed copy to keep it with the lease.',
  agreementOpen: 'Open agreement',
  agreementCreate: 'Set up agreement',
  agreementUploaded: 'Uploaded copy',
  agreementUnavailable: "Couldn't load the agreement.",
  raiseRequest: 'Raise request',
  ledgerCaption: 'Rent ledger, one row per rent period',
  paymentsCaption: 'Payments made on this lease',
  colFor: 'For',
  colDate: 'Date',
  colMethod: 'Method',
  colAmount: 'Amount',
  leftToPay: (amount: string) => `${amount} left`,
  noPayments: 'No payments yet',
  noPaymentsDesc: 'Payments on this lease will appear here.',
  viewReceipt: (month: string) => `Download the receipt for ${month}`,
  shareReceiptFor: (month: string) => `Share the receipt for ${month}`,
  recordFor: (month: string) => `Record a payment for ${month}`,
  payFor: (month: string) => `Pay rent for ${month}`,
  acceptFor: (month: string) => `Accept the payment for ${month}`,
  historyTitle: 'Lease history',
  historySubtitle: 'View your past and cancelled leases',
  historyEmpty: 'No lease history found',
  historyEmptyDesc: 'Expired or cancelled leases will appear here',
  historyViewLease: 'View lease',
  historyLoadMore: 'Load more',
  frequencyLabels: { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', annually: 'Annually' } as Record<string, string>,
  frequencyShort: { daily: '/ day', weekly: '/ week', monthly: '/ month', annually: '/ year' } as Record<string, string>,
  periodLabels: { daily: 'Day', weekly: 'Week', monthly: 'Month', annually: 'Year' } as Record<string, string>,
  typeLabels: { residential: 'Residential', commercial: 'Commercial', industrial: 'Industrial', institutional: 'Institutional', land: 'Land' } as Record<string, string>,
})
